import { motion } from "framer-motion";
import { TShirt } from "@phosphor-icons/react";

const MOOD_CLOTHING = {
  stressed: [
    { hex: "#A8C4D6", name: "Powder Blue", reason: "Calms the nervous system and lowers cortisol through cool-tone visual grounding." },
    { hex: "#84A98C", name: "Sage Green", reason: "Evokes nature and restoration, helping your body release physical tension." },
    { hex: "#B8C9B8", name: "Soft Mint", reason: "Gentle on the eyes, signaling safety and ease to your subconscious." },
  ],
  anxious: [
    { hex: "#EAE4D9", name: "Warm Oat", reason: "Neutral warmth wraps you in a sense of stability and calm familiarity." },
    { hex: "#CDB4DB", name: "Soft Lavender", reason: "Eases mental tension through its association with rest and spiritual calm." },
    { hex: "#D4C5B9", name: "Sand Beige", reason: "Grounding earth tone that anchors scattered energy back to the present." },
  ],
  burned_out: [
    { hex: "#E76F51", name: "Warm Coral", reason: "Re-energizes depleted vitality through gentle warmth without overwhelming." },
    { hex: "#E9C46A", name: "Golden Yellow", reason: "Activates solar plexus energy, rekindling inner motivation and optimism." },
    { hex: "#F4A261", name: "Amber Orange", reason: "Stimulates creativity and emotional warmth to counter numbness." },
  ],
  sad: [
    { hex: "#E07A5F", name: "Terracotta", reason: "Warm undertones gently uplift mood by stimulating emotional warmth." },
    { hex: "#F4A261", name: "Soft Orange", reason: "Encourages social connection and feelings of comfort and belonging." },
    { hex: "#D4726A", name: "Dusty Rose", reason: "Balances sadness with gentle heart-opening energy and self-compassion." },
  ],
  low_confidence: [
    { hex: "#2D6A4F", name: "Emerald", reason: "Activates personal power and signals competence and self-assurance." },
    { hex: "#722F37", name: "Burgundy", reason: "Deep, grounded authority that commands respect and inner strength." },
    { hex: "#1B3A5C", name: "Navy Sapphire", reason: "Projects quiet confidence and intellectual depth without arrogance." },
  ],
  happy: [
    { hex: "#FFD166", name: "Sunshine Gold", reason: "Amplifies joy by radiating warmth and optimism outward to others." },
    { hex: "#F4A261", name: "Peach Nectar", reason: "Sustains positive energy with playful warmth that invites connection." },
    { hex: "#CDB4DB", name: "Lilac Dream", reason: "Elevates happiness into a state of gentle euphoria and creativity." },
  ],
  calm: [
    { hex: "#84A98C", name: "Forest Sage", reason: "Deepens your calm by connecting you to nature's restorative rhythm." },
    { hex: "#A8DADC", name: "Sky Blue", reason: "Expands the feeling of spaciousness and mental clarity." },
    { hex: "#EAE4D9", name: "Warm Cream", reason: "Sustains peaceful energy with soft, unobtrusive warmth." },
  ],
  energized: [
    { hex: "#E76F51", name: "Vibrant Coral", reason: "Channels your energy into confident, magnetic presence." },
    { hex: "#2D6A4F", name: "Deep Teal", reason: "Balances high energy with grounded focus so you don't burn out." },
    { hex: "#E9C46A", name: "Bright Marigold", reason: "Keeps creative and social energy flowing at its peak." },
  ],
  tired: [
    { hex: "#F4A261", name: "Warm Amber", reason: "Gently re-energizes without overstimulating drained senses." },
    { hex: "#E9C46A", name: "Soft Gold", reason: "Activates subtle vitality and motivation through warm solar tones." },
    { hex: "#84A98C", name: "Sage Green", reason: "Restores energy through connection to nature's renewing cycle." },
  ],
  frustrated: [
    { hex: "#A8C4D6", name: "Cool Blue", reason: "Counteracts heated frustration with cooling, centering energy." },
    { hex: "#84A98C", name: "Eucalyptus", reason: "Diffuses irritation through nature-associated calm and clarity." },
    { hex: "#CDB4DB", name: "Pale Lilac", reason: "Softens sharp edges of anger into gentler emotional processing." },
  ],
  grateful: [
    { hex: "#2D6A4F", name: "Deep Teal", reason: "Deepens heart-centered gratitude with grounded, expansive energy." },
    { hex: "#FFD166", name: "Warm Gold", reason: "Radiates your gratitude outward, making it contagious to others." },
    { hex: "#D4C5B9", name: "Linen Beige", reason: "Grounds gratitude in simplicity and presence." },
  ],
  hopeful: [
    { hex: "#A8DADC", name: "Dawn Blue", reason: "Mirrors the promise of a new day and fresh possibilities." },
    { hex: "#E9C46A", name: "Sunbeam", reason: "Nurtures hope with warmth that feels like possibility opening up." },
    { hex: "#84A98C", name: "New Leaf Green", reason: "Symbolizes growth and the unfolding of something beautiful." },
  ],
};

function getClothingForMood(mood) {
  if (!mood) return MOOD_CLOTHING.calm;
  const key = mood.toLowerCase().replace(/\s+/g, "_");
  if (MOOD_CLOTHING[key]) return MOOD_CLOTHING[key];
  if (key.includes("stress") || key.includes("overwhelm") || key.includes("tense")) return MOOD_CLOTHING.stressed;
  if (key.includes("anxi") || key.includes("worry") || key.includes("nervous")) return MOOD_CLOTHING.anxious;
  if (key.includes("burn") || key.includes("exhaust")) return MOOD_CLOTHING.burned_out;
  if (key.includes("confiden") || key.includes("doubt") || key.includes("insecure")) return MOOD_CLOTHING.low_confidence;
  if (key.includes("sad") || key.includes("down") || key.includes("low")) return MOOD_CLOTHING.sad;
  return MOOD_CLOTHING.calm;
}

export default function WhatToWear({ mood, compact = false }) {
  const colors = getClothingForMood(mood);

  return (
    <div data-testid="what-to-wear-section">
      {!compact && (
        <div className="flex items-center gap-2 mb-3">
          <TShirt size={20} weight="duotone" className="text-[#84A98C]" />
          <span className="text-sm font-medium text-[#1A2421]">What to Wear</span>
        </div>
      )}
      <div className={`${compact ? "flex gap-2 overflow-x-auto hide-scrollbar" : "space-y-2.5"} ${!compact ? "pl-7" : ""}`}>
        {colors.map((c, i) => (
          <motion.div
            key={c.hex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className={compact
              ? "shrink-0 w-36 rounded-2xl border border-black/5 p-3"
              : "flex items-start gap-3 rounded-2xl border border-black/5 p-3"
            }
            style={{ backgroundColor: c.hex + "0C" }}
            data-testid={`clothing-swatch-${i}`}
          >
            <div
              className={`rounded-xl border border-black/5 shrink-0 ${compact ? "w-full h-10 mb-2" : "w-10 h-10"}`}
              style={{ backgroundColor: c.hex }}
            />
            <div className={compact ? "" : "flex-1 min-w-0"}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-medium text-[#1A2421] ${compact ? "text-[11px]" : "text-xs"}`}>{c.name}</span>
                <span className="text-[9px] text-[#78857F] font-mono">{c.hex}</span>
              </div>
              <p className={`text-[#78857F] leading-snug mt-0.5 ${compact ? "text-[9px]" : "text-[11px]"}`}>{c.reason}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export { getClothingForMood, MOOD_CLOTHING };
